import { AnimatePresence, motion } from 'motion/react';
import type { GameState } from '../../../../modules/game/LootBoxOpening';

export interface PurchaseOverlayProps {
  state: GameState;
  onClose: () => void;
  onRetry: () => void;
}

/**
 * Full-page overlay shown during the buy round-trip and on failure.
 * Success path closes the overlay and hands off to <RevealScreen> via
 * the host's `onRevealReady` URL update — so there's nothing to render
 * here past `'purchasing'`.
 */
export function PurchaseOverlay({ state, onClose, onRetry }: PurchaseOverlayProps) {
  const visible = state.step !== 'idle';

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="purchase-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="
            fixed inset-0 z-50 flex items-center justify-center
            bg-black/70 backdrop-blur-md
            px-4
          "
          role="dialog"
          aria-modal="true"
        >
          <PhaseContent state={state} onClose={onClose} onRetry={onRetry} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function PhaseContent({ state, onClose, onRetry }: PurchaseOverlayProps) {
  if (state.step === 'purchasing') {
    return <PurchasingSpinner />;
  }
  if (state.step === 'failed') {
    return <FailureScreen message={state.message} onRetry={onRetry} onClose={onClose} />;
  }
  return null;
}

function PurchasingSpinner() {
  return (
    <div className="flex flex-col items-center gap-4 text-white">
      <motion.div
        className="size-14 rounded-full border-[3px] border-white/30 border-t-[color:var(--ui-accent)]"
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, duration: 0.9, ease: 'linear' }}
        aria-hidden
      />
      <p className="type-headline font-semibold">Opening pack…</p>
      <p className="type-subhead opacity-70">Please hold on.</p>
    </div>
  );
}

function FailureScreen({
  message,
  onRetry,
  onClose,
}: {
  message: string;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.25 }}
      className="
        w-full max-w-[440px] rounded-[28px] p-6 text-white
        border border-[rgba(171,174,179,0.24)]
        bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0.02)_100%)]
        backdrop-blur-[20px]
      "
    >
      <h2 className="type-headline font-bold mb-1">Purchase failed</h2>
      <p className="type-subhead opacity-80 mb-6 break-words">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-full px-5 py-2.5 type-body-semi border border-white/30"
        >
          Close
        </button>
        <button
          type="button"
          onClick={onRetry}
          className="
            rounded-full px-5 py-2.5 type-body-semi
            bg-[color:var(--ui-accent)] text-[color:var(--ui-button-text)]
          "
        >
          Try again
        </button>
      </div>
    </motion.div>
  );
}
